import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import IconButton from '@material-ui/core/IconButton';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@afosto/components-react/Popper/Popper';
import createDataSourceUrl from '../DataSource/helpers/createDataSourceUrl';
import useDataSourceContext from '../DataSource/useDataSourceContext';
import Icon from '../Icon';
import translations from './translations';

const GridMenuItem = React.forwardRef(({ onClick, icon, name, disabled }, ref) => (
  <MenuItem ref={ref} onClick={onClick} disabled={disabled}>
    {icon && (
      <ListItemIcon>
        <Icon icon={icon} />
      </ListItemIcon>
    )}
    <Typography variant="inherit">{name}</Typography>
  </MenuItem>
));

GridMenuItem.propTypes = {
  onClick: PropTypes.func,
  icon: PropTypes.array,
  name: PropTypes.string,
  disabled: PropTypes.bool,
};

GridMenuItem.defaultProps = {
  onClick: undefined,
  icon: undefined,
  name: '',
  disabled: false,
};

const TooltipMenuItem = ({ onClick, icon, name, title }) => {
  const [isOpen, setIsOpen] = useState(false);

  /**
   * Handle opening the menu when clicking the menu item.
   */
  const handleOpenMenu = useCallback(() => {
    setIsOpen(true);
    onClick();
  }, [setIsOpen, onClick]);

  /**
   * Handle closing the menu
   */
  const handleCloseMenu = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <ClickAwayListener onClickAway={handleCloseMenu}>
      <div>
        <Tooltip
          PopperProps={{
            disablePortal: true,
          }}
          placement="left"
          onClose={handleCloseMenu}
          open={isOpen}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={title}
        >
          <GridMenuItem onClick={handleOpenMenu} icon={icon} name={name} />
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
};

TooltipMenuItem.propTypes = {
  onClick: PropTypes.func,
  icon: PropTypes.array,
  name: PropTypes.string,
  title: PropTypes.string,
};

TooltipMenuItem.defaultProps = {
  onClick: undefined,
  icon: undefined,
  name: '',
  title: '',
};

const DataGridActions = ({ actions }) => {
  const intl = useIntl();
  const [gridActionsIsOpen, setGridActionsIsOpen] = useState(false);
  const gridActionsButtonRef = useRef(null);
  const dataSource = useDataSourceContext();
  const {
    activeItem: activeItemModule = {},
    search: searchModule = {},
    copyGridUrl,
    dataExport,
    columnSelection,
    advancedSort,
    importData,
    setShowColumnsSelection,
    showColumnsSelection,
    showAdvancedSort,
    setShowAdvancedSort,
    showDataExportSelection,
    setShowDataExportSelection,
  } = dataSource;
  const { activeItem } = activeItemModule;
  const { searchValue } = searchModule;

  /**
   * Create the grid url and copy it to clipboard.
   */
  const handleCopyGridUrl = () => {
    const url = createDataSourceUrl(dataSource);
    navigator.clipboard.writeText(url);
  };

  /**
   * Close the grid actions menu.
   * @param {Event} event
   */
  const handleCloseGridActions = event => {
    if (gridActionsButtonRef.current.contains(event.target)) {
      return;
    }

    setGridActionsIsOpen(false);
  };

  const copyGridUrlAvailable = copyGridUrl && navigator.clipboard && navigator.clipboard.writeText;
  const columnSelectionActions = [
    {
      key: 'columnSelection',
      icon: ['fal', 'columns'],
      name: intl.formatMessage(translations.gridActions.columnSelection.title),
      onClick: () => {
        setShowColumnsSelection(!showColumnsSelection);
      },
    },
  ];
  const dataExportActions = [
    {
      key: 'dataExport',
      icon: ['fal', 'download'],
      name: intl.formatMessage(translations.gridActions.dataExport.title),
      onClick: () => {
        setShowDataExportSelection(!showDataExportSelection);
      },
    },
  ];
  const advancedSortActions = [
    {
      key: 'advancedSort',
      icon: ['fal', 'sort'],
      name: intl.formatMessage(translations.gridActions.sorting.title),
      onClick: () => {
        setShowAdvancedSort(!showAdvancedSort);
      },
      disabled: searchValue?.length > 0,
    },
  ];
  const dataImportActions = [
    {
      key: 'dataImport',
      icon: ['fal', 'upload'],
      name: intl.formatMessage(translations.gridActions.dataImport.title),
      onClick: () => {},
    },
  ];
  const gridActions = [
    ...actions,
    ...(columnSelection ? columnSelectionActions : []),
    ...(dataExport ? dataExportActions : []),
    ...(advancedSort ? advancedSortActions : []),
    ...(importData ? dataImportActions : []),
  ].map(({ key, icon, name, onClick, disabled }) => ({
    key,
    icon,
    name,
    disabled,
    onClick: () => {
      if (gridActionsIsOpen) {
        setGridActionsIsOpen(false);
      }

      if (onClick) {
        onClick(dataSource);
      }
    },
  }));

  if (!gridActions.length && !copyGridUrlAvailable) {
    return null;
  }

  return (
    <div>
      {!activeItem &&
        !!gridActions.length &&
        gridActions.slice(0, 2).map(({ key, icon, onClick, disabled }) => (
          <IconButton key={`shortcut_action_${key}`} onClick={onClick} disabled={disabled}>
            <Icon icon={icon} />
          </IconButton>
        ))}
      <IconButton
        onClick={() => setGridActionsIsOpen(!gridActionsIsOpen)}
        buttonRef={gridActionsButtonRef}
      >
        <Icon icon={['fal', 'ellipsis-v']} />
      </IconButton>
      <Popper
        handleClose={handleCloseGridActions}
        anchorEl={gridActionsButtonRef}
        open={gridActionsIsOpen}
        position="bottom-end"
      >
        <ClickAwayListener onClickAway={handleCloseGridActions}>
          <MenuList>
            {gridActions.map(({ key, icon, name, onClick, disabled }) => (
              <GridMenuItem
                key={`grid_action_${key}`}
                icon={icon}
                name={name}
                onClick={onClick}
                disabled={disabled}
              />
            ))}
            {copyGridUrlAvailable && (
              <TooltipMenuItem
                icon={['fal', 'copy']}
                name={intl.formatMessage(translations.gridActions.copyGridUrl.title)}
                onClick={handleCopyGridUrl}
                title={intl.formatMessage(translations.gridActions.copyGridUrl.tooltip)}
              />
            )}
          </MenuList>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

DataGridActions.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      icon: PropTypes.array,
      onClick: PropTypes.func,
    }),
  ),
};

DataGridActions.defaultProps = {
  actions: [],
};

export default DataGridActions;
